import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import ReactToolTip from 'react-tooltip';
import _ from 'lodash';
import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import Page from '@lwt-helix/page';
import { Button } from '@lwt-helix/buttons';

import Schema from './ValidationSchema';
import Routes from '../../constants/Routes';
import {
    LinkSuccess,
    LinkConflict,
    InviteLandingGenericFailed,
    InvalidInvitationMessage
} from './SuccessAndFailureCases';
import { fetchInvitation, linkInvitationPost } from '../../api/AuthenticationApi';
import { Status } from '../../constants/Invitations';
import { parseISO, differenceInCalendarDays } from 'date-fns';

const DataEntry = ({ register, errors }) => {
    return <div className="form-row">
        <div className="col-md-6">
            <div className="form-group my-0">
                <label htmlFor="firstName">First Name</label>
                <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    className="form-control-md form-control"
                    maxLength={24}
                    {...register("firstName")}
                />
            </div>
            <small className="text-danger">
                {errors?.firstName && errors.firstName.message}
            </small>
        </div>
        <div className="col-md-6">
            <div className="form-group my-0">
                <label htmlFor="lastName">Last Name</label>
                <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    className="form-control-md form-control"
                    maxLength={24}
                    {...register("lastName")}
                />
            </div>
            <small className="text-danger">
                {errors?.lastName && errors.lastName.message}
            </small>
        </div>
        <div className="col-md-6">
            <div className="form-group">
                <label htmlFor="password1">Create Password</label>
                <input
                    id="password1"
                    name="password1"
                    type="password"
                    className="form-control-md form-control"
                    onCopy={() => false}
                    data-tip
                    data-for="passwordTip"
                    {...register("password1")}
                />
                <ReactToolTip id="passwordTip" place="top" effect="solid" type="error">
                    Cannot have more than 2 consecutive identical characters
                    <br />(eg 111).<br />
                    Requires at least 3 of the 4 following types:
                    <ul>
                        <li>A lower-case letter</li>
                        <li>An upper-case letter</li>
                        <li>A number</li>
                        <li>A special character (!@#$%^&*)</li>
                    </ul>
                </ReactToolTip>
                <small className="text-danger">
                    {errors?.password1 && errors.password1.message}
                </small>
            </div>
        </div>
        <div className="col-md-6">
            <div className="form-group">
                <label htmlFor="password1">Re-Enter Password</label>
                <input
                    id="password2"
                    name="password2"
                    type="password"
                    className="form-control-md form-control"
                    {...register("password2")} />
                <small className="text-danger">
                    {errors?.password2 && "Passwords must match"}
                </small>
            </div>
        </div>
    </div>;
};

const InviteLandingPage = () => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const { register, formState, handleSubmit, setValue } = useForm({ resolver: yupResolver(Schema) });
    const linkSuccessful = useSelector(state => state.authenticationReducer.linkSuccess);
    const loading = useSelector(state => state.authenticationReducer.isLoading);
    const invitation = useSelector(state => state.authenticationReducer.invitation);
    const submitError = useSelector(state => state.authenticationReducer.error);
    const handleRegistration = useCallback(data => {
        dispatch(linkInvitationPost(id, invitation.emailAddress, data.password1, data.firstName, data.lastName));
    }, [id, invitation?.emailAddress]);
    const [isSubmissionCompleted, setIsSubmissionCompleted] = useState(false);

    const { errors } = formState;

    useEffect(() => {
        dispatch(fetchInvitation(id));
    }, [id, dispatch]);

    useEffect(() => {
        handleSubmit(handleRegistration)();
    }, [handleSubmit, handleRegistration]);

    useEffect(() => {
        if (invitation) {
            if (invitation.status?.toUpperCase() !== Status.INVITED
                || differenceInCalendarDays(new Date(), parseISO(invitation.date)) > 7) {
                setIsSubmissionCompleted(true);
            }
            setValue("firstName", invitation.firstName ?? "", { shouldValidate: true });
            setValue("lastName", invitation.lastName ?? "", { shouldValidate: true });
            setValue("email", invitation.emailAddress ?? "");
        }
    }, [invitation]);

    return invitation && (
        <Page fluid hero dataLwtId={'invitations-landing-page'}>
            {isSubmissionCompleted
                ? <InvalidInvitationMessage />
                : <form
                    className={"mx-auto my-6 rounded card-raised p-6 card card-body"}
                    style={{ minWidth: '50vw', width: '300px' }}
                    onSubmit={handleSubmit(handleRegistration)}
                >
                    <img className="mx-auto mb-3" src="https://assets.lwolf.com/img/lw-logo-dark.svg" alt="Lone Wolf"
                        height="90" />
                    <h3 className="w-100 text-center">You've been invited to create a Lone Wolf Account</h3>
                    <hr className="my-4 border-top" />
                    <h4 className="w-100 text-center text-primary mb-0">{invitation.emailAddress}</h4>
                    <hr className="my-4 border-top" />
                    {linkSuccessful ? <LinkSuccess /> :
                        <>
                            <DataEntry register={register} errors={errors} />
                            <Button
                                color="primary"
                                type="submit"
                                disabled={!_.isEmpty(errors) || loading}
                                style={{ width: "100%" }}
                                dataLwtId="register-account-button"
                            >
                                Submit
                        </Button>
                            <hr className="my-4 border-top" />
                            {submitError == null ?
                                <p className="text-muted d-block w-100 text-center">
                                    Already Have a Lone Wolf Account? <a href={Routes.LINK_ACCOUNT.replace(":id", id)}>Link
                                Account</a>
                                </p> :
                                submitError?.response?.status === 409
                                    ? <LinkConflict invitationId={id} />
                                    : <InviteLandingGenericFailed invitationId={id} />
                            }
                        </>
                    }
                </form>
            }
        </Page>
    );
};


export default InviteLandingPage;

